import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, noop } from 'underscore';

import {
  useDefaultEditorStyle,
  useFloatingBannerMessage,
} from '@bingads-webui-campaign-react/smart-component-util';
import { ChoiceGroup } from '@fluentui/react/lib/ChoiceGroup';
import { Label } from '@fluentui/react/lib/Label';
import {
  useSiteConfig,
  DataServiceContext,
  useInstrumentationServiceContext,
  useEditorStatusContext,
  editorStatusLoadingWrapperForPromise,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  facebookPageInfoFilter,
  batchTransformImagesInComponent,
  importFromFacebook,
  hasFBOAuthError,
} from '@bingads-webui-campaign-react/smart-page-facebook-import-dialog';
import { createErrorMessage } from '@bingads-webui-campaign/error-utilities';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';

import { useUploadService } from './components/use-upload-service';
import { getImageOptions } from './components/common-image-options';
import { getImageOptionsForPivot } from './components/common-image-options-for-pivot';
import { ImageOptionsKey } from './components/consts';

export const SmartImageEditor = ({
  label,
  className,
  getImageFromFacebookInfo,
  imageOptionsConfig,
  i18n,
  onNewImageUploaded,
  selectedKey,
  uploadedFileInfo,
  facebookImageInfo,
  onChoiceChange,
  enableFacebookImport,
  displayAsPivot,
  stockImageInfo,
  onUseStockImageOption,
}) => {
  const {
    sectionTitleClassName,
    choiceGroupClassName,
    editorPivotStyleContentClassName,
    uploadButtonClassName,
  } = useDefaultEditorStyle();
  const { siteConfig } = useSiteConfig();
  const { smartPageService, hostSystemScenario } = useContext(DataServiceContext);
  const [, dispatchEditorStatus] = useEditorStatusContext();
  const { imageService } = useContext(DataServiceContext);
  const { uploadImageAsync } = useUploadService();
  const { showWarning } = useFloatingBannerMessage(dispatchEditorStatus);
  const logger = useInstrumentationServiceContext();

  const onFileSelected = (selectedFileInfo) => {
    const { imageData, file } = selectedFileInfo || {};
    if (imageData) {
      uploadImageAsync(imageData)
        .then((imageMetaData) => {
          onNewImageUploaded(imageMetaData, file);
        })
        .catch(() => {
          showWarning(i18n.getString(_TL_('The image failed to upload. Please try again')));
        });
    }
  };

  const onOptionChangeInternal = (...args) => {
    let option;

    if (displayAsPivot) {
      const [item] = args;
      option = { key: item.props.itemKey };
    } else {
      [, option] = args;
    }

    const selectedOption = option.key;

    if (selectedOption === ImageOptionsKey.FACEBOOKIMPORT && enableFacebookImport) {
      if (facebookImageInfo) {
        onNewImageUploaded(facebookImageInfo);
      } else {
        importFromFacebook(
          siteConfig,
          smartPageService,
          facebookPageInfoFilter.basic,
          hostSystemScenario
        )
          .finally(editorStatusLoadingWrapperForPromise(dispatchEditorStatus))
          .then((fbSite) => {
            const image = getImageFromFacebookInfo(fbSite);

            return image ? batchTransformImagesInComponent(imageService, [image]) : null;
          })
          .then((imageInfos) => {
            if (imageInfos === null) {
              showWarning(i18n.getString(_TL_('We were unable to find your Facebook page cover photo. Please select a stock image above or upload your own instead.')));
            } else if (!isEmpty(imageInfos)) {
              onNewImageUploaded(imageInfos[0]);
            } else {
              showWarning(i18n.getString(_TL_('The image failed to upload. Please try again')));
            }
          })
          .catch((error) => {
            if (hasFBOAuthError(error)) {
              logger.logTrace('Encounter Facebook OAuth error while importing FB posts');
              showWarning(i18n.getString(_TL_('We could not access your Facebook profile, please reconnect it in Settings.')));
            } else {
              const err = createErrorMessage(error);
              logger.logError(`Import logo image from facebook failed. ${err}`);
              showWarning(i18n.getString(_TL_('We were not able to find images from this Facebook page. Please try again or reimport your Facebook Page.')));
            }
          });
      }
    } else if (selectedOption === ImageOptionsKey.UPLOAD) {
      if (uploadedFileInfo && uploadedFileInfo.image && uploadedFileInfo.file) {
        onNewImageUploaded(uploadedFileInfo.image, uploadedFileInfo.file);
      }
    } else if (selectedOption === ImageOptionsKey.DEFAULT && displayAsPivot) {
      if (stockImageInfo) {
        onUseStockImageOption(stockImageInfo);
      }
    }

    onChoiceChange(selectedOption);
    logger.logTrace(`SmartImageEditor|OptionChanged|${selectedOption}`);
  };

  const finalImageOptions = displayAsPivot ?
    getImageOptionsForPivot({
      imageOptionsConfig,
      i18n,
      onFileSelected,
      uploadedFileInfo,
      classes: {
        editorPivotStyleContentClassName,
        uploadButtonClassName,
      },
    })
    : getImageOptions({
      i18n,
      imageOptionsConfig,
      enableFacebookImport,
      onFileSelected,
      uploadedFileInfo,
    });

  const sectionTitleLabelId = 'image-options-label';

  return (
    <div className={className}>
      {
        !displayAsPivot &&
        <Label id={sectionTitleLabelId} className={sectionTitleClassName} >
          <span>{label}</span>
        </Label>
      }
      {
        displayAsPivot ?
          <Pivot
            defaultSelectedKey={selectedKey}
            onLinkClick={onOptionChangeInternal}
          >
            {
              finalImageOptions.map(option => (
                <PivotItem
                  headerText={i18n.getString(option.text)}
                  itemKey={option.key}
                  key={option.key}
                  alwaysRender
                >
                  {option.onRenderItemLink()}
                </PivotItem>
              ))
            }
          </Pivot>
          : <ChoiceGroup
            selectedKey={selectedKey}
            options={finalImageOptions}
            onChange={onOptionChangeInternal}
            ariaLabelledBy={sectionTitleLabelId}
            className={choiceGroupClassName}
          />
      }
    </div>
  );
};

SmartImageEditor.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  getImageFromFacebookInfo: PropTypes.func.isRequired,
  imageOptionsConfig: PropTypes.shape({
    [ImageOptionsKey.DEFAULT]: PropTypes.shape({
      text: PropTypes.string.isRequired,
    }).isRequired,
    [ImageOptionsKey.FACEBOOKIMPORT]: PropTypes.shape({
      text: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onNewImageUploaded: PropTypes.func.isRequired,
  selectedKey: PropTypes.string.isRequired,
  uploadedFileInfo: PropTypes.shape({
    file: PropTypes.shape({
      name: PropTypes.string,
    }),
    image: PropTypes.shape({
      IdString: PropTypes.string,
    }),
  }),
  facebookImageInfo: PropTypes.shape({
    imageId: PropTypes.string,
  }),
  stockImageInfo: PropTypes.shape({
    externalSrc: PropTypes.string,
    image: PropTypes.shape({
      IdString: PropTypes.string,
    }),
  }),
  onUseStockImageOption: PropTypes.func,
  onChoiceChange: PropTypes.func.isRequired,
  enableFacebookImport: PropTypes.bool.isRequired,
  displayAsPivot: PropTypes.bool,
};

SmartImageEditor.defaultProps = {
  className: null,
  facebookImageInfo: null,
  uploadedFileInfo: null,
  displayAsPivot: false,
  stockImageInfo: null,
  onUseStockImageOption: noop,
};
